import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { subDays } from 'date-fns'
import clsx from 'clsx'
import {
  NOTIFICATION_EVENT_TYPES,
  NOTIFICATION_SOURCES,
  type NotificationOutboxRow,
  type NotificationSource,
  listNotificationOutbox,
  resendPush,
} from '@/api/notifications'
import { emptyTableCellClassName } from '@/constants/ui'
import { formatMoscowDateTime } from '@/utils/format'
import { USE_MOCK } from '@/config'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { LoadingSecondsBanner } from '@/components/ui/LoadingSecondsBanner'
import { Button } from '@/components/ui/Button'
import { SearchInput } from '@/components/ui/SearchInput'
import { PillToggle } from '@/components/ui/PillToggle'
import { roleLabelForTemplate } from '@/utils/notification-ui'
import { NotificationOutboxDrawer } from '@/components/notifications/NotificationOutboxDrawer'
import i18n from '@/utils/i18n'

function sourceLabel(s: string): string {
  if (s === 'trigger') return i18n.t('notif.source.trigger')
  if (s === 'broadcast') return i18n.t('notif.source.broadcast')
  if (s === 'manual_resend') return i18n.t('notif.source.manual_resend')
  return s
}

export function NotificationLogPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const defaultRange = useMemo(() => {
    const to = new Date()
    const from = subDays(to, 7)
    return { from: from.toISOString(), to: to.toISOString() }
  }, [])
  const [createdFrom, setCreatedFrom] = useState(() => defaultRange.from.slice(0, 10))
  const [createdTo, setCreatedTo] = useState(() => defaultRange.to.slice(0, 10))
  const [eventTypes, setEventTypes] = useState<Set<string>>(() => new Set())
  const [sources, setSources] = useState<Set<string>>(() => new Set())
  const [errorsOnly, setErrorsOnly] = useState(true)
  const [search, setSearch] = useState('')
  const [toast, setToast] = useState<string | null>(null)
  const [drawerRow, setDrawerRow] = useState<NotificationOutboxRow | null>(null)

  useEffect(() => {
    const bid = searchParams.get('broadcast_id')
    if (bid) {
      navigate(`/notifications/broadcasts?highlight=${encodeURIComponent(bid)}`, { replace: true })
    }
  }, [navigate, searchParams])

  const createdFromIso = useMemo(
    () => new Date(`${createdFrom}T00:00:00.000Z`).toISOString(),
    [createdFrom],
  )
  const createdToIso = useMemo(
    () => new Date(`${createdTo}T23:59:59.999Z`).toISOString(),
    [createdTo],
  )

  const eventFilterList = useMemo(() => {
    if (eventTypes.size === 0) return []
    return [...eventTypes]
  }, [eventTypes])

  const sourceFilterList = useMemo(() => {
    if (sources.size === 0) return []
    return [...sources] as NotificationSource[]
  }, [sources])

  const { data, isLoading, error, isFetching, isPlaceholderData } = useQuery({
    queryKey: [
      'notification-outbox',
      page,
      createdFromIso,
      createdToIso,
      eventFilterList,
      sourceFilterList,
      errorsOnly,
      search,
    ],
    queryFn: () =>
      listNotificationOutbox({
        page,
        createdFrom: createdFromIso,
        createdTo: createdToIso,
        eventTypes: eventFilterList,
        sources: sourceFilterList,
        errorsOnly,
        search,
      }),
    placeholderData: keepPreviousData,
  })

  const tableLoading = isFetching && (isPlaceholderData || !data)

  const resendMut = useMutation({
    mutationFn: (id: string) => resendPush(id),
    onSuccess: () => {
      setToast(t('notif.log.resentToast'))
      void qc.invalidateQueries({ queryKey: ['notification-outbox'] })
      window.setTimeout(() => setToast(null), 3500)
    },
    onError: (e: Error) => {
      setToast(e.message || t('notif.common.error'))
      window.setTimeout(() => setToast(null), 5000)
    },
  })

  const total = data?.total ?? 0
  const pageSize = 50
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const toggleEventType = (et: string) => {
    setEventTypes((prev) => {
      const n = new Set(prev)
      if (n.has(et)) n.delete(et)
      else n.add(et)
      return n
    })
    setPage(1)
  }

  const toggleSource = (s: string) => {
    setSources((prev) => {
      const n = new Set(prev)
      if (n.has(s)) n.delete(s)
      else n.add(s)
      return n
    })
    setPage(1)
  }

  const isInitialLoading = isLoading && !data
  const isBackgroundLoading = tableLoading && (data?.items.length ?? 0) > 0

  return (
    <div>
      <PageHeader title={t('notif.log.title')} subtitle={t('notif.log.subtitle', { count: total })} />
      {toast ? (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-[100] rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] px-4 py-2 text-sm shadow-lg"
        >
          {toast}
        </div>
      ) : null}
      {USE_MOCK ? (
        <p className="mb-4 text-sm text-[color:var(--stryzh-color-text-secondary)]">
          {t('notif.log.mockHint')}
        </p>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="mb-3 rounded-md border border-[color:var(--stryzh-color-status-error)] px-3 py-2 text-sm text-[color:var(--stryzh-color-status-error)]"
        >
          {(error as Error).message}
        </div>
      ) : null}

      <div className="mb-4 space-y-3">
        <SearchInput
          placeholder={t('notif.log.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onBlur={() => setPage(1)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setPage(1)
          }}
        />
        <div className="space-y-3 rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] px-3 py-2.5">
          <div className="space-y-2">
            <div className="text-xs font-medium text-[color:var(--stryzh-color-text-secondary)]">
              {t('notif.log.eventFilterHint')}
            </div>
            <div className="flex flex-wrap gap-2">
              {NOTIFICATION_EVENT_TYPES.map((et) => (
                <button
                  key={et}
                  type="button"
                  className={clsx(
                    'stryzh-filter-chip',
                    eventTypes.has(et) && 'stryzh-filter-chip--selected',
                  )}
                  onClick={() => toggleEventType(et)}
                >
                  {et}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-xs font-medium text-[color:var(--stryzh-color-text-secondary)]">
              {t('notif.log.sourceFilterHint')}
            </div>
            <div className="flex flex-wrap gap-2">
              {NOTIFICATION_SOURCES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={clsx(
                    'stryzh-filter-chip',
                    sources.has(s) && 'stryzh-filter-chip--selected',
                  )}
                  onClick={() => toggleSource(s)}
                >
                  {sourceLabel(s)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
              {t('notif.log.period')}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="h-9 rounded-[var(--stryzh-radius-lg)] border border-[var(--stryzh-color-border-default)] bg-transparent px-2 text-sm text-[var(--stryzh-color-text-primary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
                aria-label={t('notif.log.dateFrom')}
                value={createdFrom}
                onChange={(e) => {
                  setCreatedFrom(e.target.value)
                  setPage(1)
                }}
              />
              <span className="text-[var(--stryzh-color-text-secondary)]">—</span>
              <input
                type="date"
                className="h-9 rounded-[var(--stryzh-radius-lg)] border border-[var(--stryzh-color-border-default)] bg-transparent px-2 text-sm text-[var(--stryzh-color-text-primary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
                aria-label={t('notif.log.dateTo')}
                value={createdTo}
                onChange={(e) => {
                  setCreatedTo(e.target.value)
                  setPage(1)
                }}
              />
            </div>
            <PillToggle
              id="errors-only-toggle"
              checked={errorsOnly}
              onChange={(v) => {
                setErrorsOnly(v)
                setPage(1)
              }}
              label={t('notif.log.errorsOnly')}
            />
            <div className="ml-auto">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEventTypes(new Set())
                  setSources(new Set())
                  setErrorsOnly(true)
                  setCreatedFrom(defaultRange.from.slice(0, 10))
                  setCreatedTo(defaultRange.to.slice(0, 10))
                  setSearch('')
                  setPage(1)
                }}
              >
                {t('common.reset')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
        {isBackgroundLoading ? <LoadingSecondsBanner /> : null}
        <table className="w-full min-w-[960px] border-collapse text-left text-sm">
          <thead className="sticky top-0 z-10 bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2 font-medium">{t('notif.log.colSent')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.log.colEvent')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.log.colSource')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.log.colRecipient')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.log.colOrder')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.log.colDelivered')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.log.colOpened')}</th>
              <th className="px-3 py-2 font-medium">{t('notif.log.colError')}</th>
            </tr>
          </thead>
          <tbody>
            {isInitialLoading ? (
              <tr>
                <td colSpan={8} className={emptyTableCellClassName}>
                  <Loader size="sm" centered label={t('common.loading')} />
                </td>
              </tr>
            ) : (data?.items.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={8} className={emptyTableCellClassName}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              data!.items.map((r) => (
                <tr
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  className={clsx(
                    'cursor-pointer border-t border-[color:var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]',
                    tableLoading && 'opacity-70',
                  )}
                  onClick={() => setDrawerRow(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setDrawerRow(r)
                    }
                  }}
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.sent_at ? formatMoscowDateTime(r.sent_at) : '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.event_type}</td>
                  <td className="px-3 py-2 text-[color:var(--stryzh-color-text-primary)]">{sourceLabel(r.source)}</td>
                  <td className="px-3 py-2">
                    <div>{r.recipient_full_name ?? '—'}</div>
                    <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                      {r.recipient_role ? roleLabelForTemplate(r.recipient_role) : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {r.order_id ? (
                      <Link
                        className="text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                        to={`/orders/${r.order_id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.order_label ?? r.order_id.slice(0, 8)}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">{r.delivered_at ? '✓' : '—'}</td>
                  <td className="px-3 py-2 text-center">{r.opened_at ? '✓' : '—'}</td>
                  <td className="max-w-[180px] truncate px-3 py-2 text-xs text-[color:var(--stryzh-color-status-error)]">
                    {r.error ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            className="rounded border border-[var(--stryzh-color-border-default)] px-3 py-1 text-sm disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('notif.common.prev')}
          </button>
          <span className="text-sm text-[var(--stryzh-color-text-secondary)]">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="rounded border border-[var(--stryzh-color-border-default)] px-3 py-1 text-sm disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            {t('notif.common.next')}
          </button>
        </div>
      ) : null}

      <NotificationOutboxDrawer
        open={Boolean(drawerRow)}
        row={drawerRow}
        onClose={() => setDrawerRow(null)}
        resendPending={resendMut.isPending}
        onResend={(id) => {
          resendMut.mutate(id)
        }}
      />
    </div>
  )
}
